const PUBLIC_SIGNATURE_QUERY_PARAM = "kiraahPublicSignature";
const SIGNATURE_GATEWAY_QUERY_PARAM = "kiraahSignatureGateway";
const PUBLIC_LEARNING_CAMPAIGN_QUERY_PARAM = "kiraahPublicLearningCampaign";
const LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM = "kiraahLearningCampaignGateway";
const PUBLIC_LEARNING_CAMPAIGN_PAGE_QUERY_PARAM = "kiraahPublicLearningCampaignPage";
const PUBLIC_PRESENCE_QUERY_PARAM = "kiraahPublicPresence";
const PRESENCE_GATEWAY_QUERY_PARAM = "kiraahPresenceGateway";

const PUBLIC_PATH_PREFIXES = [
  "e-signature/public/",
  "e-signature/gateway/",
  "presence/public/",
  "presence/sign/",
  "presence/gateway/",
  "learning-campaign/public/",
  "learning-campaign/gateway/",
] as const;

const GATEWAY_HASH_TO_PUBLIC = [
  { gatewayPrefix: "#/e-signature/gateway/", publicPrefix: "#/e-signature/public/" },
  { gatewayPrefix: "#/learning-campaign/gateway/", publicPrefix: "#/learning-campaign/public/" },
  { gatewayPrefix: "#/presence/gateway/", publicPrefix: "#/presence/public/" },
] as const;

type PublicEntryQueryConfig = {
  queryParams: readonly string[];
  resolveRoute: (encodedToken: string, params: URLSearchParams) => string;
};

const PUBLIC_ENTRY_QUERY_CONFIGS: PublicEntryQueryConfig[] = [
  {
    queryParams: [PUBLIC_SIGNATURE_QUERY_PARAM, SIGNATURE_GATEWAY_QUERY_PARAM],
    resolveRoute: (encodedToken) => `e-signature/public/${encodedToken}`,
  },
  {
    queryParams: [PUBLIC_LEARNING_CAMPAIGN_QUERY_PARAM, LEARNING_CAMPAIGN_GATEWAY_QUERY_PARAM],
    resolveRoute: (encodedToken, params) => {
      const page = params.get(PUBLIC_LEARNING_CAMPAIGN_PAGE_QUERY_PARAM)?.trim();
      return page
        ? `learning-campaign/public/${encodedToken}/${page}`
        : `learning-campaign/public/${encodedToken}`;
    },
  },
  {
    queryParams: [PUBLIC_PRESENCE_QUERY_PARAM, PRESENCE_GATEWAY_QUERY_PARAM],
    resolveRoute: (encodedToken) => `presence/public/${encodedToken}`,
  },
];

function encodePublicRouteToken(token: string): string {
  try {
    return encodeURIComponent(decodeURIComponent(token));
  } catch {
    return encodeURIComponent(token);
  }
}

export function resolvePublicEntryTarget(
  origin: string,
  route: string,
  useHashRouting: boolean
): string {
  return useHashRouting ? `${origin}/#/${route}` : `/${route}`;
}

function resolveQueryEntryToken(params: URLSearchParams): {
  encodedToken: string;
  route: string;
} | null {
  for (const config of PUBLIC_ENTRY_QUERY_CONFIGS) {
    for (const queryParam of config.queryParams) {
      const token = params.get(queryParam)?.trim();
      if (!token) {
        continue;
      }

      const encodedToken = encodePublicRouteToken(token);
      return {
        encodedToken,
        route: config.resolveRoute(encodedToken, params),
      };
    }
  }

  return null;
}

function resolvePublicPathRoute(): string | null {
  const normalizedPath = window.location.pathname.replace(/^\/+/, "");
  if (!normalizedPath) {
    return null;
  }

  return PUBLIC_PATH_PREFIXES.find((prefix) => normalizedPath.startsWith(prefix)) ?? null;
}

declare global {
  interface Window {
    __KIRAAH_SKIP_ANGULAR_BOOTSTRAP__?: boolean;
  }
}

/** True when Angular must not bootstrap yet (hard navigation pending). */
export function needsPublicEntryHardNavigation(useHashRouting: boolean): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.__KIRAAH_SKIP_ANGULAR_BOOTSTRAP__) {
    return true;
  }

  if (resolveQueryEntryToken(new URLSearchParams(window.location.search))) {
    return true;
  }

  return useHashRouting && resolvePublicPathRoute() !== null;
}

function markSkipAngularBootstrap(): void {
  window.__KIRAAH_SKIP_ANGULAR_BOOTSTRAP__ = true;
}

function navigateToPublicEntryTarget(route: string, useHashRouting: boolean): boolean {
  const target = useHashRouting
    ? `${window.location.origin}/#/${route}`
    : `${window.location.origin}/${route}`;

  if (window.location.href === target) {
    return false;
  }

  markSkipAngularBootstrap();
  window.location.replace(target);
  return true;
}

/**
 * Email/QR links may use public or gateway query params when hash fragments are stripped.
 * Promote to an in-app route before Angular bootstraps the router.
 */
export function promotePublicEntryRoute(useHashRouting: boolean): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const resolved = resolveQueryEntryToken(new URLSearchParams(window.location.search));
  if (!resolved) {
    return false;
  }

  return navigateToPublicEntryTarget(resolved.route, useHashRouting);
}

/** @deprecated Use resolvePublicEntryTarget for signature-only callers in tests. */
export function resolvePublicSignatureEntryTarget(
  origin: string,
  token: string,
  useHashRouting: boolean
): string {
  const encodedToken = encodePublicRouteToken(token);
  return resolvePublicEntryTarget(origin, `e-signature/public/${encodedToken}`, useHashRouting);
}

/**
 * Legacy links may still open `#/…/gateway/{token}` on the mobile PWA.
 * Route directly to the public screen.
 */
export function promoteHashGatewayRouteToPublic(): void {
  if (typeof window === "undefined") {
    return;
  }

  const hash = window.location.hash;
  for (const { gatewayPrefix, publicPrefix } of GATEWAY_HASH_TO_PUBLIC) {
    if (!hash.startsWith(gatewayPrefix)) {
      continue;
    }

    const token = hash.slice(gatewayPrefix.length).trim();
    if (!token) {
      return;
    }

    window.history.replaceState(null, "", `${publicPrefix}${token}`);
    return;
  }
}

/**
 * Azure Blob static websites serve index.html on deep paths via 404-document.
 * Promote `/module/public/{token}` to `/#/module/public/{token}` before bootstrap.
 */
export function promotePathPublicRouteToHash(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const normalizedPath = resolvePublicPathRoute();
  if (!normalizedPath) {
    return false;
  }

  return navigateToPublicEntryTarget(normalizedPath, true);
}

/**
 * Local dev uses path-based routing while email links still use `/#/route`.
 * Promote `#/path` to `/path` before Angular bootstraps the router.
 */
export function promoteHashRouteToPath(): void {
  if (typeof window === "undefined") {
    return;
  }

  const hash = window.location.hash;
  if (!hash.startsWith("#/")) {
    return;
  }

  const pathRoute = hash.slice(1);
  if (!pathRoute.startsWith("/")) {
    return;
  }

  window.history.replaceState(null, "", pathRoute);
}
